import { useEffect, useRef, useState } from 'react';

import { getAccessToken } from '@/api/client';
import { foldFrame, type LiveSample } from '@/lib/metrics';
import type { LiveEvent, MetricFrame } from '@/types';

export type { LiveSample } from '@/lib/metrics';

export interface LiveState {
  connected: boolean;
  error: string | null;
  samples: LiveSample[];
  latest: LiveSample | null;
  streamCount: number;
}

const MAX_SAMPLES = 120; // ~2 min at 1 Hz

function wsUrl(sessionId: string, token: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/live/${sessionId}?token=${encodeURIComponent(token)}`;
}

/**
 * Subscribe to /ws/live for a session. Frames arrive per-stream per-role at
 * ~1 Hz; foldFrame buckets them by second (shared with the historical view).
 */
export function useLiveMetrics(sessionId: string | undefined, enabled: boolean): LiveState {
  const [state, setState] = useState<LiveState>({
    connected: false,
    error: null,
    samples: [],
    latest: null,
    streamCount: 0,
  });
  const streamIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!sessionId || !enabled) return;

    let ws: WebSocket | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let closed = false;
    streamIds.current = new Set();

    const ingest = (f: MetricFrame) => {
      streamIds.current.add(f.stream_id);
      setState((prev) => {
        const samples = prev.samples.slice();
        const cur = foldFrame(samples, f);
        if (samples.length > MAX_SAMPLES) samples.shift();
        return { ...prev, samples, latest: cur, streamCount: streamIds.current.size };
      });
    };

    const connect = () => {
      if (closed) return;
      // Read the token at connect time, not once at effect setup: right after
      // a page (re)load the silent auth refresh may not have populated it yet.
      // Bailing out permanently there is why the live view stayed blank until
      // the session ended (REST calls survive via 401→refresh→retry, but this
      // WS is one-shot). Retry until the token is available.
      const token = getAccessToken();
      if (!token) {
        retry = setTimeout(connect, 500);
        return;
      }
      const sock = new WebSocket(wsUrl(sessionId, token));
      ws = sock;
      sock.onopen = () => {
        // If the effect was already torn down (React StrictMode runs it
        // twice in dev: setup → cleanup → setup), close this now-open socket
        // instead of leaving a zombie that never delivers.
        if (closed) {
          sock.close();
          return;
        }
        setState((p) => ({ ...p, connected: true, error: null }));
      };
      sock.onmessage = (ev) => {
        if (closed) return;
        let msg: LiveEvent;
        try {
          msg = JSON.parse(ev.data as string) as LiveEvent;
        } catch {
          return;
        }
        if (msg.event === 'metric_frame') ingest(msg);
        else if (msg.event === 'server.shutdown')
          setState((p) => ({ ...p, error: 'server is shutting down' }));
      };
      sock.onclose = () => {
        if (closed) return;
        setState((p) => ({ ...p, connected: false }));
        retry = setTimeout(connect, 2_000);
      };
      // Let onclose drive reconnect; don't close here (would double-fire).
      sock.onerror = () => {};
    };

    connect();
    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      // Closing a socket that's still CONNECTING throws "closed before the
      // connection is established" and can wedge it. Defer the close to onopen
      // in that case; StrictMode's re-run creates a fresh socket regardless.
      if (ws) {
        if (ws.readyState === WebSocket.CONNECTING) ws.onopen = () => ws?.close();
        else ws.close();
      }
    };
  }, [sessionId, enabled]);

  return state;
}
